import React, {createContext, useEffect, useMemo, useRef} from "react";

import {
	scrollIntoView,
	scrollToElement,
	scrollToTop,
} from "../../helpers/scrollHelpers";
import {pushElementToViewport} from "./pushElementToViewport";

type Context = {
	pushElementToViewport: (
		elemId: string,
		viewportSizeCorrection?: {bottom?: number}
	) => void;
	scrollIntoView: (elem: HTMLElement | null) => void;
	scrollToElement: (elemId: string) => void;
	scrollToTop: (n?: number) => void;

	forced: () => boolean;
	withForcedScroll: <R = void>(fn: () => R) => R;
};

const ScrollContext = createContext<Context>({
	pushElementToViewport: noop,
	scrollIntoView: noop,
	scrollToElement: noop,
	scrollToTop: noop,

	forced: () => false,
	withForcedScroll: (f) => {
		return f();
	},
});

function ScrollContextProvider(props: {children: React.ReactNode}) {
	const forced = useRef(false);

	const scrollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
	const scrollStopDelay = 150;

	useEffect(() => {
		const handleScroll = () => {
			if (!forced.current) {
				return;
			}

			if (scrollTimeout.current) {
				clearTimeout(scrollTimeout.current);
			}

			scrollTimeout.current = setTimeout(() => {
				forced.current = false;
			}, scrollStopDelay);
		};

		window.addEventListener("scroll", handleScroll, {passive: true});

		return () => {
			window.removeEventListener("scroll", handleScroll);

			if (scrollTimeout.current) {
				clearTimeout(scrollTimeout.current);
			}
		};
	}, []);

	const value = useMemo<Context>(
		() => ({
			pushElementToViewport: (elemId, viewportSizeCorrection) => {
				forced.current = true;
				pushElementToViewport(elemId, viewportSizeCorrection);
			},
			scrollIntoView: (elem) => {
				forced.current = true;
				scrollIntoView(elem);
			},
			scrollToElement: (elemId) => {
				forced.current = true;
				scrollToElement(elemId);
			},
			scrollToTop: (n) => {
				forced.current = true;
				scrollToTop(n);
			},

			forced: () => forced.current,
			withForcedScroll: (fn) => {
				forced.current = true;
				return fn();
			},
		}),
		[]
	);

	return (
		<ScrollContext.Provider value={value}>
			{props.children}
		</ScrollContext.Provider>
	);
}

function noop() {}

export default ScrollContext;
export {ScrollContextProvider};
